import { type DiscoverTagCloudItem, type DiscoverTagCloudViewProps, useTranslation } from "@next-platform/client-common-core";
import React, { useCallback, useMemo } from "react";

import { LinkWrapper } from "@/components/customComponents";
import DiscoverRowHeader from "@/pages/Discover/components/DiscoverRowHeader";
import { tagNameToUrlSlug } from "@/utils/tagUrlSlug";

const TAG_LINK_CLASSNAME =
  "inline-flex items-center h-[32px] px-3.5 rounded-full border border-solid border-gray-7 text-sm cursor-pointer w-fit no-underline hover:opacity-90 bg-transparent text-gray-12 dark:text-white";

type DiscoverTagCloudLinkProps = {
  tagCloudItem: DiscoverTagCloudItem;
  onSelect: (tagCloudItem: DiscoverTagCloudItem) => void;
};

const DiscoverTagCloudLink = ({ tagCloudItem, onSelect }: DiscoverTagCloudLinkProps) => {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    onSelect(tagCloudItem);
  }, [onSelect, tagCloudItem]);

  const identifier = useMemo(() => tagNameToUrlSlug(tagCloudItem.tag), [tagCloudItem.tag]);
  const ariaLabel = t("discover:tagCloud.tagAriaLabel", { tag: tagCloudItem.label, defaultValue: "Browse {{tag}}" });

  return (
    <LinkWrapper
      data-testid={`DiscoverTagCloud-Tag-${tagCloudItem.tag}`}
      to={`/chatbot_tag/${identifier}`}
      className={TAG_LINK_CLASSNAME}
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      {tagCloudItem.label}
    </LinkWrapper>
  );
};

const MemoizedDiscoverTagCloudLink = React.memo(DiscoverTagCloudLink);

const DiscoverTagCloudView = ({ title, items, onTagSelect }: DiscoverTagCloudViewProps) => {
  return (
    <section
      data-testid="DiscoverTagCloud"
      aria-label={title}
      className="flex min-w-0 max-w-[800px] w-full mx-auto flex-col items-center gap-4 rounded-2xl border border-gray-6 dark:border-gray-6 px-6 py-8 text-center"
    >
      <DiscoverRowHeader title={title} data-testid="DiscoverTagCloud-RowHeader" />
      <ul className="m-0 flex list-none flex-wrap justify-center gap-2 p-0">
        {items.map((tagCloudItem) => (
          <li key={tagCloudItem.tag}>
            <MemoizedDiscoverTagCloudLink tagCloudItem={tagCloudItem} onSelect={onTagSelect} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default React.memo(DiscoverTagCloudView);
